import { useMemo, useState } from 'react';
import type { RootState } from '@/store';
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { CommonFormControl } from '@/components/Common/CommonFormControl';
import { BridgeTermSelector } from '@/components/LoanPrograms/BridgeTermSelector';
import { selectBridgeRatesByTerm } from '@/store/slices/hybridInterestRateSlice';
import { selectMatricesLoaded } from '@/store/slices/interestRateSlice';
import { ProductUSDAEsitmation } from '@/utils/calculator/USDA';
import { formatPrice } from '@/utils/helper';
import { computeRateQuote } from '@/utils/interestRate/quote';
import { downPaymentForProduct } from '@/utils/propertyDownPayment';
import { Product, ProductEsitmation } from '@/types/ProductType';
import { PrepaymentPenaltyModal } from '../../ConfirmModals/PrepaymentPenaltyModal';

export type ProductDetailLoanTermsProps = {
  estimation: ProductEsitmation;
  product?: Product;
  onUpdate?: () => void;
  /** When set with onBridgeTermMonthsChange, bridge term is controlled by parent (syncs useEstimation). */
  bridgeTermMonths?: number;
  onBridgeTermMonthsChange?: (months: number) => void;
};

export const ProductDetailLoanTerms = ({
  estimation,
  product,
  onUpdate,
  bridgeTermMonths: controlledBridgeTerm,
  onBridgeTermMonthsChange,
}: ProductDetailLoanTermsProps) => {
  const theme = useTheme();
  const [isPrepaymentModalOpen, setIsPrepaymentModalOpen] = useState(false);
  const [localBridgeTerm, setLocalBridgeTerm] = useState(12);
  const bridgeTerm = controlledBridgeTerm ?? localBridgeTerm;
  const setBridgeTerm = onBridgeTermMonthsChange ?? setLocalBridgeTerm;
  // Select only slices needed for computeRateQuote to avoid rerenders on unrelated state changes
  const rateState = useSelector(
    (state: RootState) => ({
      interestRate: state.interestRate,
      hybridInterestRate: state.hybridInterestRate,
    }),
    shallowEqual
  );
  const matricesLoaded = useSelector(selectMatricesLoaded);
  const backendRatesReady = Boolean(
    rateState.hybridInterestRate?.backendRates?.lastUpdated
  );

  // Cast estimation to any at the component level for accessing dynamic properties
  const estimationAny = estimation as any;

  // Get Bridge rates by term from Redux state
  const bridgeRatesByTerm = useSelector((state: RootState) =>
    selectBridgeRatesByTerm(state)
  );

  const loanAmount = useMemo(() => {
    if (!estimation) return 0;
    let loanAmount = estimation.loanAmount;
    if (estimation.type === USER_LOAN_PROGRAM.FHA) {
      loanAmount += (estimation as any).upfrontMIPfee;
    } else if (estimation.type === USER_LOAN_PROGRAM.USDA) {
      loanAmount += (estimation as ProductUSDAEsitmation).upfrontGuaranteeFee;
    }
    return loanAmount;
  }, [estimation]);

  const quote = useMemo(() => {
    if (!matricesLoaded && !backendRatesReady) return null;
    const downPayment = downPaymentForProduct(product);
    const creditScore = product?.user?.loanInfo?.creditScore ?? 700;
    const loanTermYears = estimation.loanTerm || 30;
    const offerPrice =
      product?.loanInfo?.offerPrice ?? estimationAny.offerPrice;
    const listPrice = product?.price ?? estimationAny.listPrice ?? 0;
    return computeRateQuote(rateState as RootState, {
      loanProgram: estimation.type as any,
      offerPrice,
      listPrice,
      downPayment,
      creditScore,
      loanTermYears,
      bridgeTermMonths:
        estimation.type === USER_LOAN_PROGRAM.BRIDGE ? bridgeTerm : undefined,
      estimation: estimation as any,
    });
  }, [
    matricesLoaded,
    backendRatesReady,
    rateState,
    estimation.type,
    estimation.loanTerm,
    product,
    estimationAny.offerPrice,
    estimationAny.listPrice,
    bridgeTerm,
  ]);

  if (!estimation) return null;

  return (
    <Card
      sx={{
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
        borderRadius: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Enhanced Interest Rate & APR Display (single source of truth) */}
        <Box
          sx={{
            mb: { xs: 2, sm: 2.5, md: 3 },
            p: { xs: 1.5, sm: 1.75, md: 2 },
            background: 'transparent',
            borderRadius: 2,
            border: `1px solid ${theme.palette.greyShades.border1}`,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{ color: theme.palette.text.primary }}
              >
                {quote ? `${quote.interestRate.toPrecision(4)}%` : '--'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Interest Rate
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}
              >
                {quote
                  ? quote.apr !== null
                    ? quote.apr.toPrecision(4)
                    : (quote.interestRate + 0.25).toPrecision(4)
                  : '--'}
                {quote ? '%' : ''}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                APR
              </Typography>
            </Box>
          </Box>
          {!quote && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {!matricesLoaded && !backendRatesReady
                ? 'Loading rate information...'
                : 'Rate unavailable. Sign in for live pricing or try again shortly.'}
            </Typography>
          )}
        </Box>

        <CommonFormControl
          label="Loan Amount:"
          secondLabel={formatPrice(loanAmount)}
          inLine
          labelWidth={270}
        />
        <CommonFormControl
          label="Purchase Price:"
          secondLabel={formatPrice(
            estimationAny.offerPrice || estimationAny.listPrice
          )}
          inLine
          labelWidth={270}
        />
        <CommonFormControl
          label="Monthly Payment (PITI):"
          secondLabel={formatPrice(
            estimationAny.piti || estimationAny.monthlyPayment
          )}
          inLine
          labelWidth={270}
        />
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            mb: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Typography
            sx={{
              width: { xs: '100%', sm: '270px' },
              color: theme.palette.text.secondary,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
            variant="body1"
          >
            Prepayment Penalty:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {estimation.type === USER_LOAN_PROGRAM.DSCR ? (
              (estimation as any).removePrepaymentPenalty ? (
                'Removed'
              ) : (
                <Link
                  component="button"
                  onClick={() => setIsPrepaymentModalOpen(true)}
                  sx={{
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  Yes
                </Link>
              )
            ) : (
              'No'
            )}
          </Typography>
        </Box>

        <CommonFormControl
          label="Balloon Payment:"
          secondLabel={
            estimation.type === USER_LOAN_PROGRAM.FIX_AND_FLIP ? 'Yes' : 'No'
          }
          inLine
          labelWidth={270}
          sx={{ mb: 1 }}
        />
        {/* Bridge Term Selector */}
        {estimation.type === USER_LOAN_PROGRAM.BRIDGE ? (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2,
                p: 2,
              }}
            >
              <BridgeTermSelector
                selectedTerm={bridgeTerm}
                onTermChange={setBridgeTerm}
              />
            </Box>
          </Box>
        ) : (
          <CommonFormControl
            label="Loan Term:"
            secondLabel={estimation.loanTerm + ' years'}
            inLine
            labelWidth={270}
          />
        )}
      </CardContent>

      {/* Prepayment Penalty Modal */}
      {product && onUpdate && (
        <PrepaymentPenaltyModal
          isOpen={isPrepaymentModalOpen}
          onClose={() => setIsPrepaymentModalOpen(false)}
          product={product}
          estimation={estimation}
          onUpdate={onUpdate}
        />
      )}
    </Card>
  );
};
